import { Search } from "@/components/search/Search.tsx";
import { EmployeeColumnFilterField } from "@/pages/employees/index/EmployeeColumnFilterField.tsx";
import { EmployeeColumnFilterRenderers } from "@/pages/employees/index/employee-column-filter-renderers.ts";
import { ColumnFilterValue } from "@/pages/employees/index/employee-column-filter-conditions.ts";
import {
  createEmployeeColumnFilter,
  getAvailableFilterCategories,
  getFilterColumnTableTitles,
  getFilterFieldTitle,
  reorderEmployeeColumnFilters,
  seedLifecycleStatusFilter,
} from "@/pages/employees/index/employee-column-filters-modal.helper.ts";
import { LIFECYCLE_STATUS_COLUMN } from "@/pages/employees/index/use-employee-column-filters.ts";
import {
  ApiEmployeeColumn,
  ApiEmployeeColumnFilter,
  EmployeeColumnFilterCategory,
  EmployeeColumnFilterColumn,
} from "@/types/employees/employee-column.types.ts";
import type { ApiEmployeeLifecycleStatus } from "@/types/employees/employee.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EmployeeColumnFiltersModalProps extends ModalProps {
  categories: EmployeeColumnFilterCategory[];
  columnByKey: Map<string, ApiEmployeeColumn>;
  filters: ApiEmployeeColumnFilter[];
  onApply: (filters: ApiEmployeeColumnFilter[]) => void;
  onClearAll: () => void;
  // Свои контролы значения для отдельных колонок или типов фильтра
  renderers?: EmployeeColumnFilterRenderers;
  // Статусы организации по умолчанию: действуют, пока статус не задан явно
  lifecycleStatusDefaults?: ApiEmployeeLifecycleStatus[];
}

export function EmployeeColumnFiltersModal({
  categories,
  columnByKey,
  filters,
  onApply,
  onClearAll,
  renderers,
  lifecycleStatusDefaults,
  ...props
}: EmployeeColumnFiltersModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;

  // Колонка могла пропасть из каталога или перестать фильтроваться — такой фильтр не показываем
  const availableFilters = seedLifecycleStatusFilter(
    filters.filter((filter) => columnByKey.get(filter.field)?.is_filterable ?? false),
    columnByKey.get(LIFECYCLE_STATUS_COLUMN),
    lifecycleStatusDefaults
  );
  const [draft, setDraft] = useState<ApiEmployeeColumnFilter[]>(availableFilters);
  const [editingDraft, setEditingDraft] = useState<ApiEmployeeColumnFilter[]>(availableFilters);
  const [isEditing, setIsEditing] = useState(false);
  const [editSearch, setEditSearch] = useState("");
  const [addSearch, setAddSearch] = useState("");
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const selectedFields = useMemo(
    () => new Set((isEditing ? editingDraft : draft).map((filter) => filter.field)),
    [draft, editingDraft, isEditing]
  );

  // Поле таблицы подписывается её названием: «Комментарий» есть и у позиции, и у компенсации
  const tableTitleByColumn = useMemo(() => getFilterColumnTableTitles(categories), [categories]);

  const editCategories = getAvailableFilterCategories(categories, selectedFields, editSearch);
  const addCategories = getAvailableFilterCategories(categories, selectedFields, addSearch);

  const addFilter = (column: ApiEmployeeColumn, target: "draft" | "editing") => {
    if (target === "editing") {
      setEditingDraft((current) => [...current, createEmployeeColumnFilter(column)]);
      return;
    }
    setDraft((current) => [...current, createEmployeeColumnFilter(column)]);
    setAddSearch("");
    setIsAddMenuOpen(false);
  };

  const updateValue = (field: string, value: ColumnFilterValue) =>
    setDraft((current) =>
      current.map((filter) => (filter.field === field ? { field, ...value } : filter))
    );

  const removeEditingFilter = (field: string) =>
    setEditingDraft((current) => current.filter((filter) => filter.field !== field));

  const startEditing = () => {
    setEditingDraft(draft);
    setEditSearch("");
    setIsEditing(true);
  };

  const saveEditing = () => {
    setDraft(editingDraft);
    setIsEditing(false);
    setEditSearch("");
  };

  const cancelEditing = () => {
    setEditingDraft(draft);
    setIsEditing(false);
    setEditSearch("");
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    setEditingDraft((current) =>
      reorderEmployeeColumnFilters(current, result.source.index, result.destination!.index)
    );
  };

  const handleApply = () => {
    onApply(draft);
    close(id);
  };

  const handleClearAll = () => {
    setDraft([]);
    onClearAll();
    close(id);
  };

  const renderSearchResults = (
    visibleCategories: EmployeeColumnFilterCategory[],
    onAdd: (column: ApiEmployeeColumn) => void
  ) =>
    visibleCategories.map((category) => (
      <Stack key={category.id} gap="xs">
        <Text className="text-xs font-semibold uppercase tracking-wide text-secondary">
          {category.title}
        </Text>
        {renderGroupedColumns(category.columns, (column) => (
          <Button
            key={column.id}
            variant="plain"
            size="md"
            className="w-full justify-start"
            onClick={() => onAdd(column)}
          >
            {column.title}
          </Button>
        ))}
      </Stack>
    ));

  return (
    <Modal
      {...props}
      variant="side"
      size={isEditing ? "lg" : "md"}
      title={isEditing ? t("core:edit_filters") : t("common:filter")}
      layout="stretch"
      withCloser
      closeByEscEnabled
      headerActionsSlot={
        !isEditing ? (
          <Button size="sm" variant="secondary" onClick={startEditing}>
            {t("common:edit")}
          </Button>
        ) : undefined
      }
      actions={
        isEditing ? (
          <Stack gap="sm">
            <Button size="lg" onClick={saveEditing}>
              {t("common:save")}
            </Button>
            <Button size="lg" variant="plain" onClick={cancelEditing}>
              {t("common:cancel")}
            </Button>
          </Stack>
        ) : (
          <Stack gap="sm">
            <Button size="lg" onClick={handleApply}>
              {t("common:filter_apply")}
            </Button>
            <Button size="lg" variant="plain" onClick={handleClearAll}>
              {t("common:filter_clear")}
            </Button>
          </Stack>
        )
      }
    >
      {isEditing ? (
        <div className="absolute inset-y-0 right-0 left-0 grid min-h-0 grid-cols-2 divide-x overflow-hidden border-t border-dark/10">
          <Stack gap="md" className="min-h-0 overflow-y-auto pb-6">
            <Box className="sticky top-0 z-sticky shrink-0 bg-light pb-1">
              <Search
                value={editSearch}
                placeholder={`${t("core:add_or_search_filter")}...`}
                clearable
                onClear={() => setEditSearch("")}
                onChange={(event) => setEditSearch(event.target.value)}
                innerPadding
              />
            </Box>

            <Stack className={"px-6"} gap={"sm"}>
              {editSearch.trim()
                ? renderSearchResults(editCategories, (column) => addFilter(column, "editing"))
                : editCategories.map((category) => {
                    const isExpanded = expanded[category.id] ?? false;
                    return (
                      <Stack key={category.id} gap="xs">
                        <Box
                          className="flex cursor-pointer items-center justify-between py-1"
                          onClick={() =>
                            setExpanded((current) => ({
                              ...current,
                              [category.id]: !isExpanded,
                            }))
                          }
                        >
                          <Title size="xs" header={category.title} />
                          <FontAwesomeIcon
                            icon={
                              isExpanded ? "fa-light fa-chevron-down" : "fa-light fa-chevron-right"
                            }
                          />
                        </Box>
                        {isExpanded &&
                          renderGroupedColumns(category.columns, (column) => (
                            <Stack
                              key={column.id}
                              onClick={() => addFilter(column, "editing")}
                              gap={"sm"}
                              justify={"between"}
                              className={
                                "bg-dark/5 border border-dark/5 rounded-xs p-1.75 px-4 group cursor-pointer"
                              }
                              items={"center"}
                              direction={"horizontal"}
                            >
                              <span>{column.title}</span>
                              <span className="font-semibold opacity-0 group-hover:opacity-100 text-sm text-brand-dark">
                                {t("common:add")}
                              </span>
                            </Stack>
                          ))}
                      </Stack>
                    );
                  })}
            </Stack>
          </Stack>

          <Stack className="min-h-0 overflow-y-auto pb-6 px-6 pt-4">
            <Title
              size={"xs"}
              header={t("core:your_filters")}
              className={"text-secondary"}
              paddingBottom
            />
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="employee-filters">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <Stack gap="sm">
                      {editingDraft.map((filter, index) => {
                        const column = columnByKey.get(filter.field);
                        if (!column) return null;
                        const tableTitle = tableTitleByColumn.get(column.id);
                        return (
                          <Draggable key={filter.field} draggableId={filter.field} index={index}>
                            {(dragProvided) => (
                              <Box
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                className="flex items-center gap-sm rounded-xs border border-dark/5 bg-dark/5 px-1 py-1 group"
                              >
                                <span
                                  {...dragProvided.dragHandleProps}
                                  className="flex cursor-grab items-center"
                                >
                                  <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
                                </span>
                                <Stack className="min-w-0 flex-1">
                                  <Text className="truncate font-semibold">{column.title}</Text>
                                  {tableTitle && (
                                    <Text className="truncate text-sm text-secondary">
                                      {tableTitle}
                                    </Text>
                                  )}
                                </Stack>
                                <ButtonIcon
                                  className={"opacity-0 group-hover:opacity-100"}
                                  variant="plain"
                                  size="sm"
                                  title={t("common:delete")}
                                  onClick={() => removeEditingFilter(filter.field)}
                                  icon={<FontAwesomeIcon icon="fa-light fa-xmark" />}
                                />
                              </Box>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Stack>
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Stack>
        </div>
      ) : (
        <Stack>
          {draft.map((filter) => {
            const column = columnByKey.get(filter.field);
            if (!column) return null;

            // Название таблицы попадает в подпись любого контрола, в том числе своего рендера
            const tableTitle = tableTitleByColumn.get(column.id);
            const fieldColumn = tableTitle
              ? { ...column, title: getFilterFieldTitle(column.title, tableTitle) }
              : column;

            return (
              <EmployeeColumnFilterField
                key={filter.field}
                column={fieldColumn}
                filter={filter}
                renderers={renderers}
                onChange={(value) => updateValue(filter.field, value)}
              />
            );
          })}

          <Stack direction={"horizontal"} items={"center"} justify={"center"} className={"pt-4"}>
            <DropdownMenu
              open={isAddMenuOpen}
              onOpenChange={setIsAddMenuOpen}
              closeOnClick={false}
              placement="bottom-start"
              trigger={
                <Button
                  variant="plain"
                  size="md"
                  leftIcon={<FontAwesomeIcon icon="fa-solid fa-plus" />}
                  className="font-semibold"
                >
                  {t("common:add_filter")}
                </Button>
              }
            >
              <div className=" pb-2 -mt-1 mx-2">
                <Search
                  value={addSearch}
                  placeholder={`${t("core:add_or_search_filter")}...`}
                  clearable
                  onClear={() => setAddSearch("")}
                  onChange={(event) => setAddSearch(event.target.value)}
                  onKeyDown={(event) => event.stopPropagation()}
                  hideSearchIcon
                />
              </div>
              {addSearch.trim()
                ? addCategories.flatMap((category) => [
                    <DropdownMenuLabel key={`${category.id}-label`}>
                      {category.title}
                    </DropdownMenuLabel>,
                    ...renderGroupedDropdownColumns(category.columns, (column) => (
                      <DropdownMenuItem
                        key={column.id}
                        title={column.title}
                        onClick={() => addFilter(column, "draft")}
                      />
                    )),
                  ])
                : addCategories.map((category) => (
                    <DropdownMenuSub key={category.id} title={category.title}>
                      {renderGroupedDropdownColumns(category.columns, (column) => (
                        <DropdownMenuItem
                          key={column.id}
                          title={column.title}
                          onClick={() => addFilter(column, "draft")}
                        />
                      ))}
                    </DropdownMenuSub>
                  ))}
            </DropdownMenu>
          </Stack>
        </Stack>
      )}
    </Modal>
  );
}

function renderGroupedColumns(
  columns: EmployeeColumnFilterColumn[],
  renderColumn: (column: EmployeeColumnFilterColumn) => ReactNode
) {
  const groups = groupColumns(columns);
  return groups.map((group) =>
    group.title ? (
      <Stack key={group.id} gap="xs">
        <Text className="text-xs font-semibold text-secondary">{group.title}</Text>
        {group.columns.map(renderColumn)}
      </Stack>
    ) : (
      <Stack key={group.id} gap="xs">
        {group.columns.map(renderColumn)}
      </Stack>
    )
  );
}

function renderGroupedDropdownColumns(
  columns: EmployeeColumnFilterColumn[],
  renderColumn: (column: EmployeeColumnFilterColumn) => ReactNode
) {
  return groupColumns(columns).flatMap((group) => [
    ...(group.title
      ? [<DropdownMenuLabel key={`${group.id}-label`}>{group.title}</DropdownMenuLabel>]
      : []),
    ...group.columns.map(renderColumn),
  ]);
}

function groupColumns(columns: EmployeeColumnFilterColumn[]) {
  const groups = new Map<
    string,
    { id: string; title?: string; columns: EmployeeColumnFilterColumn[] }
  >();

  columns.forEach((column) => {
    const id = column.groupId ?? "ungrouped";
    const group = groups.get(id) ?? { id, title: column.groupTitle, columns: [] };
    group.columns.push(column);
    groups.set(id, group);
  });

  return [...groups.values()];
}
